import { useCallback, useEffect, useMemo, useState } from 'react';
import { TipCardState } from '../types/tipcard';

/**
 * Chave única no localStorage onde guardamos os ids dos tip cards já dispensados.
 * Centralizar aqui garante que TODOS os tip cards do app (Express, Para você,
 * Outros, etc.) compartilhem o mesmo comportamento de "não aparecer mais".
 */
const STORAGE_KEY = 'newstore:dismissed-tips';

function readDismissed(): string[] {
  // Durante o SSR não há `window`; retorna vazio e hidrata depois no client.
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string')
      : [];
  } catch {
    // JSON inválido ou acesso bloqueado (modo privado): ignora.
    return [];
  }
}

function writeDismissed(ids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Quota cheia ou storage indisponível: persistência é best-effort.
  }
}

/**
 * Gerencia os tip cards dispensados com persistência em localStorage.
 *
 * Ao clicar em "Entendi", o tip é marcado como dispensado e gravado no
 * localStorage, então ele não volta a aparecer mesmo após recarregar a tela.
 *
 * Retorna:
 * - `dismissedTips`: Set com os ids dispensados;
 * - `tipCardState`: formato consumido pelo cálculo de posicionamento;
 * - `dismissTip` / `resetTip`: para dispensar ou reexibir um tip.
 */
export function useDismissedTips() {
  // Inicia vazio para casar com o HTML renderizado no servidor (evita
  // hydration mismatch) e hidrata do localStorage logo após montar.
  const [dismissedTips, setDismissedTips] = useState<Set<string>>(new Set());

  useEffect(() => {
    const stored = readDismissed();
    if (stored.length) setDismissedTips(new Set(stored));
  }, []);

  const dismissTip = useCallback((tipId: string) => {
    setDismissedTips((prev) => {
      if (prev.has(tipId)) return prev;
      const next = new Set(prev);
      next.add(tipId);
      writeDismissed([...next]);
      return next;
    });
  }, []);

  const resetTip = useCallback((tipId: string) => {
    setDismissedTips((prev) => {
      if (!prev.has(tipId)) return prev;
      const next = new Set(prev);
      next.delete(tipId);
      writeDismissed([...next]);
      return next;
    });
  }, []);

  // Deriva o TipCardState (consumido pelo placement) a partir do Set.
  const tipCardState: TipCardState = useMemo(() => {
    const state: TipCardState = {};
    dismissedTips.forEach((id) => {
      state[id] = { dismissed: true };
    });
    return state;
  }, [dismissedTips]);

  return { dismissedTips, tipCardState, dismissTip, resetTip };
}
